/**
 * Owner 11.10.2026: after a night with the PC asleep an SFTP connection still
 * showed a green dot, and opening a folder failed with "[Session(-43)] Timeout
 * waiting for status message". The server had dropped it long before.
 *
 * Rust now checks idle sessions (src-tauri/src/ftp/health.rs) and emits
 * `ftp:connection_lost` for one that no longer answers; `ftp_check` asks at
 * once. Here that becomes the `lost` state: the dot stops being green, the pane
 * says "Connection lost" with Reconnect, and clicking the row reconnects —
 * what FileZilla and WinSCP do.
 */
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';

export const CONNECTION_LOST_EVENT = 'ftp:connection_lost';

/**
 * The store change for "conn `connId` was lost", or null when it no longer
 * applies: only a connection the UI shows as connected can be lost — one being
 * reconnected or disconnected already moved on.
 */
export function lostPatch(state, connId, reason) {
  if (!connId || state.connState[connId] !== 'connected') return null;
  const remotePanes = { ...state.remotePanes };
  delete remotePanes[connId];
  return {
    connState: { ...state.connState, [connId]: 'lost' },
    connError: { ...state.connError, [connId]: reason ? String(reason) : null },
    remotePanes,
  };
}

/** Asks Rust whether `connId` still answers; a dead one becomes `lost`. */
export async function checkConnection(store, connId) {
  if (!isTauri() || store.getState().connState[connId] !== 'connected') return true;
  let alive = true;
  try {
    alive = await invoke('ftp_check', { connId });
  } catch {
    return true; // the check itself failed: say nothing rather than guess
  }
  if (!alive) markLost(store, connId, null);
  return alive;
}

export function markLost(store, connId, reason) {
  store.setState((s) => lostPatch(s, connId, reason) || {});
}

let subscription = null;

/**
 * Listens for the lost event for the rest of the app's life (once). Imported
 * lazily, like chatNotification.js: the store is loaded by many tests that
 * never touch Tauri events.
 */
export function watchConnectionLost(store) {
  if (subscription || !isTauri()) return;
  subscription = import('../utils/safeTauriEvent')
    .then(({ subscribeTauriEvent }) =>
      subscribeTauriEvent(CONNECTION_LOST_EVENT, (evt) => {
        const { connId, reason } = evt?.payload || {};
        markLost(store, connId, reason);
      })
    )
    .catch((e) => {
      subscription = null;
      console.warn('[ftp] could not listen for lost connections:', e);
    });
}
